import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import {
  FormFieldDef,
  FormStepDef,
  InputType,
} from '../../../interfaces/form-config.interface';
import { SectionSelect } from './SectionSelect';
import { OptionsEditor } from './OptionsEditor';
import { TranslationsEditor } from './TranslationsEditor';
import { GroupPicker } from './GroupPicker';
import { IconUploader } from './IconUploader';
import { OPTION_BASED, ALL_INPUT_TYPES } from './form-config.utils';
import { useConfirm } from '../../../hooks/useConfirm';

// ── Option preset constants ────────────────────────────────────────────────

const BUILD_CONDITION_OPTIONS = [
  { value: 'New Build', label: 'New Build', translations: { en: 'New Build', 'zh-Hant': '新建' } },
  { value: 'Pre-Owned', label: 'Pre-Owned', translations: { en: 'Pre-Owned', 'zh-Hant': '二手' } },
];
const STATUS_CONDITION_OPTIONS = [
  { value: 'New', label: 'New', translations: { en: 'New', 'zh-Hant': '全新' } },
  { value: 'Used', label: 'Used', translations: { en: 'Used', 'zh-Hant': '二手' } },
  { value: 'Refurbished', label: 'Refurbished', translations: { en: 'Refurbished', 'zh-Hant': '翻新' } },
];

// ── Props ──────────────────────────────────────────────────────────────────

interface FieldDrawerProps {
  field: FormFieldDef | null;
  saving: boolean;
  allSteps: FormStepDef[];
  allGroups: string[];
  allFieldKeys: string[];
  listingSections: string[];
  detailSections: string[];
  onSave: (fieldKey: string, patch: Partial<FormFieldDef>) => void;
  onSetStatus: (fieldKey: string, isActive: boolean) => void;
  onDelete: (fieldKey: string) => void;
  onClose: () => void;
  onDirtyChange: (isDirty: boolean) => void;
}

// ── Component ──────────────────────────────────────────────────────────────

export const FieldDrawer = ({
  field,
  saving,
  allSteps,
  allGroups,
  allFieldKeys,
  listingSections,
  detailSections,
  onSave,
  onSetStatus,
  onDelete,
  onClose,
  onDirtyChange,
}: FieldDrawerProps) => {
  const { t } = useTranslation('common');
  const { confirm, ConfirmDialog } = useConfirm();
  const [local, setLocal] = useState<FormFieldDef | null>(field);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [showSuffix, setShowSuffix] = useState(false);
  const [showUnitPair, setShowUnitPair] = useState(false);
  const [showRemark, setShowRemark] = useState(false);
  const [showTranslations, setShowTranslations] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Reset local state when field key changes
  useEffect(() => {
    setLocal(field);
    setShowPlaceholder(false);
    setShowSuffix(false);
    setShowRemark(false);
    setShowTranslations(false);
    setShowAdvanced(false);
  }, [field?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track dirty state
  useEffect(() => {
    if (!field || !local) {
      onDirtyChange(false);
      return;
    }
    const isDirty = JSON.stringify(local) !== JSON.stringify(field);
    onDirtyChange(isDirty);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]); // field is read but intentionally omitted; onDirtyChange is a new inline fn each render

  if (!field || !local) return null;

  const set = (patch: Partial<FormFieldDef>) => setLocal((p) => p ? { ...p, ...patch } : p);

  const handleInputTypeChange = (newType: InputType) => {
    const patch: Partial<FormFieldDef> = { inputType: newType };
    if (!OPTION_BASED.has(newType)) patch.options = undefined;
    if (OPTION_BASED.has(newType) && !(local.options?.length)) {
      patch.options = [{ value: '', label: '', translations: { en: '', 'zh-Hant': '' } }];
    }
    if (newType !== InputType.TEXTBOX && newType !== InputType.DYNAMIC_TEXTBOX && newType !== InputType.DYNAMIC_CHECK_TEXTBOX) patch.maxLength = undefined;
    if (newType !== InputType.IMAGE_UPLOADER && newType !== InputType.IMAGE_VIDEO_UPLOADER) patch.minPhotos = undefined;
    set(patch);
  };

  // Which fields the AI chat assistant sees — same filter as buildSchemaContext
  // in Mobile/api/src/modules/ai-chat/ai-chat.service.ts.
  const usedByAiSearch =
    local?.isActive !== false &&
    [2, 3].includes(local?.step ?? 0) &&
    !!(local?.isFilterable || local?.isSearchable);

  const handleSave = async () => {
    if (!await confirm(t('confirm_save_field'))) return;
    // null (not undefined) on every clearable field below: undefined is dropped
    // by JSON.stringify, so a cleared value never reaches the backend's partial
    // Object.assign and the old DB value survives. null survives JSON and nulls
    // the (nullable) column. Mirrors listingSection/detailSection below.
    const enRemark = local.translations?.['en']?.remark || local.remark || null;
    const patch: Partial<FormFieldDef> = {
      required: local.required,
      visible: local.visible,
      label: local.translations?.['en']?.label ?? local.label,
      placeholder: local.translations?.['en']?.placeholder || local.placeholder || null,
      remark: enRemark,
      aiHint: local.aiHint || null,
      inputType: local.inputType,
      step: local.step,
      isFilterable: local.isFilterable,
      isSortable: local.isSortable,
      isSearchable: local.isSearchable,
      isListFilter: local.isListFilter,
      allowMultiple: local.allowMultiple,
      group: local.group || null,
      icon: local.icon || null,
      translations: {
        ...local.translations,
        en: {
          ...local.translations?.['en'],
          remark: enRemark ?? undefined,
        },
      },
      listingSection: local.listingSection || null,
      detailSection: local.detailSection || null,
      // null (not undefined) so a cleared condition survives JSON.stringify and
      // reaches the backend Object.assign to actually null the column.
      visibleWhen: local.visibleWhen || null,
    };
    if (local.options !== undefined) patch.options = local.options;
    if (local.inputType === InputType.AI_BUTTON) {
      patch.linkedFields = local.linkedFields ?? [];
      patch.targetField = local.targetField || null;
    }
    if (local.inputType === InputType.TEXTBOX || local.inputType === InputType.DYNAMIC_TEXTBOX || local.inputType === InputType.DYNAMIC_CHECK_TEXTBOX) {
      patch.maxLength = local.maxLength ?? null;
    }
    if (local.inputType === InputType.NUMERIC_TEXTBOX || local.inputType === InputType.TEXTBOX) {
      patch.unitPairKey = local.unitPairKey || null;
      patch.unitFactor = local.unitFactor ?? null;
      patch.unitDecimals = local.unitDecimals ?? null;
      const enSuffix = local.translations?.['en']?.suffix || local.suffix || null;
      const zhSuffix = local.translations?.['zh-Hant']?.suffix || undefined;
      patch.suffix = enSuffix;
      patch.translations = {
        ...local.translations,
        ...patch.translations,
        en: { ...local.translations?.['en'], ...patch.translations?.en, suffix: enSuffix ?? undefined },
        'zh-Hant': { ...local.translations?.['zh-Hant'], ...patch.translations?.['zh-Hant'], suffix: zhSuffix },
      };
    }
    if (local.inputType === InputType.IMAGE_UPLOADER || local.inputType === InputType.IMAGE_VIDEO_UPLOADER) {
      patch.minPhotos = local.minPhotos ?? null;
    }
    if (local.key === 'condition') {
      patch.isBuildCondition = local.isBuildCondition ?? false;
      patch.isStatusCondition = local.isStatusCondition ?? false;
    }
    onSave(field.key, patch);
  };

  const hasOptions = OPTION_BASED.has(local.inputType);

  const inputCls =
    'w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none';
  const labelCls = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

  return (
    <div className="flex flex-col w-[480px] min-h-0 border-l-4 border-l-blue-500 bg-gray-50 dark:bg-gray-900/80">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <span className="font-mono text-xs text-gray-500 dark:text-gray-400 truncate mr-2">
          {field.key}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg leading-none shrink-0"
          aria-label="Close drawer"
        >
          ✕
        </button>
      </div>

      {/* ── Scrollable body ───────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">

        {/* Listing column badge */}
        {local.listingColumn && (
          <div className="rounded-md border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30 px-3 py-2">
            <p className="text-xs font-semibold text-sky-700 dark:text-sky-300 mb-0.5">
              col: <span className="font-mono">{local.listingColumn}</span>
            </p>
            <p className="text-[11px] text-sky-600 dark:text-sky-400">
              This field is stored directly in the listing table
            </p>
          </div>
        )}

        {/* Labels — EN + ZH */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Label (EN)</label>
            <input
              type="text"
              className={inputCls}
              value={local.translations?.['en']?.label ?? local.label}
              onChange={(e) =>
                set({
                  translations: {
                    ...local.translations,
                    en: { ...local.translations?.['en'], label: e.target.value },
                  },
                })
              }
            />
          </div>
          <div>
            <label className={labelCls}>Label (ZH)</label>
            <input
              type="text"
              className={inputCls}
              value={local.translations?.['zh-Hant']?.label ?? ''}
              onChange={(e) =>
                set({
                  translations: {
                    ...local.translations,
                    'zh-Hant': { ...local.translations?.['zh-Hant'], label: e.target.value },
                  },
                })
              }
            />
          </div>
        </div>

        {/* Placeholder (collapsible) */}
        <div>
          <button
            type="button"
            onClick={() => setShowPlaceholder((v) => !v)}
            className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
          >
            {showPlaceholder ? '▼' : '▶'} Placeholder
          </button>
          {showPlaceholder && (
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Placeholder (EN)</label>
                <input
                  type="text"
                  className={inputCls}
                  value={local.translations?.['en']?.placeholder ?? local.placeholder ?? ''}
                  onChange={(e) =>
                    set({
                      translations: {
                        ...local.translations,
                        en: { ...local.translations?.['en'], placeholder: e.target.value },
                      },
                    })
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Placeholder (ZH)</label>
                <input
                  type="text"
                  className={inputCls}
                  value={local.translations?.['zh-Hant']?.placeholder ?? ''}
                  onChange={(e) =>
                    set({
                      translations: {
                        ...local.translations,
                        'zh-Hant': { ...local.translations?.['zh-Hant'], placeholder: e.target.value },
                      },
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* Suffix — unit shown after the input (NUMERIC_TEXTBOX / TEXTBOX only) */}
        {(local.inputType === InputType.NUMERIC_TEXTBOX || local.inputType === InputType.TEXTBOX) && (
          <div>
            <button
              type="button"
              onClick={() => setShowSuffix((v) => !v)}
              className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
            >
              {showSuffix ? '▼' : '▶'} Suffix <span className="text-gray-400">(e.g. {"'ft'"})</span>
            </button>
            {showSuffix && (
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Suffix (EN)</label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="e.g. ft"
                    value={local.translations?.['en']?.suffix ?? local.suffix ?? ''}
                    onChange={(e) =>
                      set({
                        translations: {
                          ...local.translations,
                          en: { ...local.translations?.['en'], suffix: e.target.value },
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <label className={labelCls}>Suffix (ZH)</label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="例：呎"
                    value={local.translations?.['zh-Hant']?.suffix ?? ''}
                    onChange={(e) =>
                      set({
                        translations: {
                          ...local.translations,
                          'zh-Hant': { ...local.translations?.['zh-Hant'], suffix: e.target.value },
                        },
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Unit pair — links two fields holding the same quantity in different units */}
        {(local.inputType === InputType.NUMERIC_TEXTBOX || local.inputType === InputType.TEXTBOX) && (
          <div>
            <button
              type="button"
              onClick={() => setShowUnitPair((v) => !v)}
              className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
            >
              {showUnitPair ? '▼' : '▶'} {t('unit_pair')}
            </button>
            {showUnitPair && (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-gray-400">{t('unit_pair_help')}</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>{t('unit_pair_key')}</label>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="loa"
                      value={local.unitPairKey ?? ''}
                      onChange={(e) => set({ unitPairKey: e.target.value || null })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>{t('unit_factor')}</label>
                    <input
                      type="number"
                      step="any"
                      className={inputCls}
                      placeholder="1"
                      value={local.unitFactor ?? ''}
                      onChange={(e) => set({ unitFactor: e.target.value === '' ? null : Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>{t('unit_decimals')}</label>
                    <input
                      type="number"
                      min="0"
                      max="4"
                      className={inputCls}
                      placeholder="1"
                      value={local.unitDecimals ?? ''}
                      onChange={(e) => set({ unitDecimals: e.target.value === '' ? null : Number(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Remark — helper text shown below the input in the sell form (collapsible) */}
        <div>
          <button
            type="button"
            onClick={() => setShowRemark((v) => !v)}
            className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
          >
            {showRemark ? '▼' : '▶'} Remark <span className="text-gray-400">(helper text in app)</span>
          </button>
          {showRemark && (
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Remark (EN)</label>
                <input
                  type="text"
                  className={inputCls}
                  placeholder="e.g. Enter the total engine power in kW"
                  value={local.translations?.['en']?.remark ?? local.remark ?? ''}
                  onChange={(e) =>
                    set({
                      remark: e.target.value || undefined,
                      translations: {
                        ...local.translations,
                        en: { ...local.translations?.['en'], remark: e.target.value || undefined },
                      },
                    })
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Remark (ZH)</label>
                <input
                  type="text"
                  className={inputCls}
                  placeholder="例：輸入引擎總功率（千瓦）"
                  value={local.translations?.['zh-Hant']?.remark ?? ''}
                  onChange={(e) =>
                    set({
                      translations: {
                        ...local.translations,
                        'zh-Hant': { ...local.translations?.['zh-Hant'], remark: e.target.value || undefined },
                      },
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* Type + Step */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Input Type</label>
            <select
              className={inputCls}
              value={local.inputType}
              onChange={(e) => handleInputTypeChange(e.target.value as InputType)}
            >
              {ALL_INPUT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Step</label>
            <select
              className={inputCls}
              value={local.step}
              onChange={(e) => set({ step: Number(e.target.value) })}
            >
              {allSteps.map((s) => (
                <option key={s.stepNumber} value={s.stepNumber}>Step {s.stepNumber}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Max Length — TEXTBOX / DYNAMIC_TEXTBOX / DYNAMIC_CHECK_TEXTBOX only */}
        {(local.inputType === InputType.TEXTBOX || local.inputType === InputType.DYNAMIC_TEXTBOX || local.inputType === InputType.DYNAMIC_CHECK_TEXTBOX) && (
          <div>
            <label className={labelCls}>Max Length <span className="text-gray-400">(optional)</span></label>
            <input
              type="number"
              min={1}
              className={inputCls}
              placeholder="e.g. 70"
              value={local.maxLength ?? ''}
              onChange={(e) => set({ maxLength: e.target.value ? parseInt(e.target.value, 10) : undefined })}
            />
            <p className="mt-1 text-xs text-gray-400">Leave blank for no limit.</p>
          </div>
        )}

        {/* Min Photos — IMAGE_UPLOADER / IMAGE_VIDEO_UPLOADER only */}
        {(local.inputType === InputType.IMAGE_UPLOADER || local.inputType === InputType.IMAGE_VIDEO_UPLOADER) && (
          <div>
            <label className={labelCls}>Min Photos <span className="text-gray-400">(optional)</span></label>
            <input
              type="number"
              min={1}
              className={inputCls}
              placeholder="e.g. 3"
              value={local.minPhotos ?? ''}
              onChange={(e) => set({ minPhotos: e.target.value ? parseInt(e.target.value, 10) : undefined })}
            />
            <p className="mt-1 text-xs text-gray-400">Minimum number of images required before submitting.</p>
          </div>
        )}

        {/* Group */}
        <div>
          <label className={labelCls}>Group</label>
          <GroupPicker
            value={local.group ?? undefined}
            existingGroups={allGroups}
            onChange={(g) => set({ group: g })}
          />
        </div>

        {/* Visible When */}
        <div className="rounded-lg p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900 space-y-2">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">Visible When</p>
          <p className="text-[11px] text-amber-600 dark:text-amber-400">
            Field is hidden unless a sibling field equals a specific value.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Sibling field</label>
              <select
                className={inputCls}
                value={local.visibleWhen?.split('=')[0] ?? ''}
                onChange={(e) => {
                  const condKey = e.target.value;
                  const condVal = local.visibleWhen?.split('=').slice(1).join('=') ?? '';
                  set({ visibleWhen: condKey ? `${condKey}=${condVal}` : undefined });
                }}
              >
                <option value="">— always visible —</option>
                {allFieldKeys
                  .filter((k) => k !== field.key)
                  .map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Equals value</label>
              <input
                type="text"
                className={inputCls}
                placeholder="e.g. Hourly, true"
                disabled={!local.visibleWhen?.split('=')[0]}
                value={local.visibleWhen?.split('=').slice(1).join('=') ?? ''}
                onChange={(e) => {
                  const condKey = local.visibleWhen?.split('=')[0] ?? '';
                  set({ visibleWhen: condKey ? `${condKey}=${e.target.value}` : undefined });
                }}
              />
            </div>
          </div>
          {local.visibleWhen && (
            <p className="text-[11px] font-mono text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 rounded px-2 py-0.5">
              {local.visibleWhen}
            </p>
          )}
          {local.visibleWhen && (
            <button
              type="button"
              className="text-[11px] text-red-400 hover:text-red-600"
              onClick={() => set({ visibleWhen: undefined })}
            >
              Clear condition
            </button>
          )}
        </div>

        {/* Layout Sections */}
        <div className="rounded-lg p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900 space-y-3">
          <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">Layout Sections</p>
          <SectionSelect
            label="Listing Section"
            value={local.listingSection}
            options={listingSections}
            locked={local.layoutFixed}
            onChange={(v) => set({ listingSection: v })}
          />
          <SectionSelect
            label="Detail Section"
            value={local.detailSection}
            options={detailSections}
            locked={local.detailFixed}
            onChange={(v) => set({ detailSection: v })}
          />
        </div>

        {/* Flags — 2-col checkbox grid */}
        <div className="grid grid-cols-3 gap-x-4 gap-y-2">
          {[
            { key: 'required', label: 'Required', locked: false },
            { key: 'visible', label: 'Visible', locked: false },
            { key: 'isFilterable', label: 'Filterable', locked: field.layoutFixed },
            { key: 'isSortable', label: 'Sortable', locked: field.layoutFixed },
            { key: 'isSearchable', label: 'Searchable', locked: field.layoutFixed },
            { key: 'isListFilter', label: 'List Filter', locked: false },
            { key: 'allowMultiple', label: 'Allow Multiple', locked: false },
          ].map(({ key, label, locked }) => (
            <label
              key={key}
              className={`flex items-center gap-2 text-sm select-none ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                className="rounded"
                checked={!!local[key as keyof FormFieldDef]}
                disabled={locked}
                onChange={(e) => set({ [key]: e.target.checked } as Partial<FormFieldDef>)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>

        {/* AI Hint — only for fields the AI chat can actually search on. Mirrors
            buildSchemaContext in Mobile/api: active, step 2-3, filterable or
            searchable. Hidden elsewhere so nobody writes a hint that is never read. */}
        {usedByAiSearch && (
          <div>
            <label className={labelCls}>
              AI Hint <span className="text-gray-400">(for AI search only, not shown in app)</span>
            </label>
            <textarea
              rows={3}
              className={`${inputCls} resize-y`}
              placeholder="e.g. Length overall, bow to stern, excluding bowsprit — not beam or waterline length"
              value={local.aiHint ?? ''}
              onChange={(e) => set({ aiHint: e.target.value || undefined })}
            />
          </div>
        )}

        {/* AI Button config — shown only for AI_BUTTON */}
        {local.inputType === InputType.AI_BUTTON && (
          <div className="rounded-lg p-3 bg-purple-50 dark:bg-purple-950/30 border border-purple-100 dark:border-purple-900 space-y-3">
            <p className="text-xs font-semibold text-purple-700 dark:text-purple-300">
              AI Button Config
            </p>

            {/* Target field */}
            <div>
              <label className={labelCls}>
                Target Field <span className="text-purple-500">(receives AI output)</span>
              </label>
              <select
                className={inputCls}
                value={local.targetField ?? ''}
                onChange={(e) => set({ targetField: e.target.value || undefined })}
              >
                <option value="">— none —</option>
                {allFieldKeys
                  .filter((k) => k !== field.key)
                  .map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
              </select>
            </div>

            {/* Source fields */}
            <div>
              <label className={labelCls}>
                Source Fields <span className="text-purple-500">(values sent to AI)</span>
              </label>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 max-h-48 overflow-y-auto">
                {allFieldKeys
                  .filter((k) => k !== field.key)
                  .map((k) => {
                    const checked = (local.linkedFields ?? []).includes(k);
                    return (
                      <label
                        key={k}
                        className="flex items-center gap-1.5 text-xs cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={checked}
                          onChange={(e) => {
                            const current = local.linkedFields ?? [];
                            set({
                              linkedFields: e.target.checked
                                ? [...current, k]
                                : current.filter((x) => x !== k),
                            });
                          }}
                        />
                        <span className="font-mono truncate">{k}</span>
                      </label>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* Options */}
        {hasOptions && (
          <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <OptionsEditor
              options={local.options ?? []}
              onChange={(opts) => set({ options: opts })}
            />
          </div>
        )}

        {/* Condition Preset — only for condition fields */}
        {local.key === 'condition' && (
          <div className="rounded-lg p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900 space-y-2">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">Condition Preset</p>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded"
                checked={!!local.isBuildCondition}
                onChange={(e) => {
                  if (e.target.checked) {
                    set({ isBuildCondition: true, isStatusCondition: false, options: BUILD_CONDITION_OPTIONS });
                  } else {
                    set({ isBuildCondition: false });
                  }
                }}
              />
              <span>Build Condition (New Build / Pre-Owned)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded"
                checked={!!local.isStatusCondition}
                onChange={(e) => {
                  if (e.target.checked) {
                    set({ isStatusCondition: true, isBuildCondition: false, options: STATUS_CONDITION_OPTIONS });
                  } else {
                    set({ isStatusCondition: false });
                  }
                }}
              />
              <span>Status Condition (New / Used / Refurbished)</span>
            </label>
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              Checking a preset clears existing options and writes the preset rows. Manually editing options after does not clear the flag.
            </p>
          </div>
        )}

        {/* Extra Translations (collapsible) */}
        <div>
          <button
            type="button"
            onClick={() => setShowTranslations((v) => !v)}
            className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
          >
            {showTranslations ? '▼' : '▶'} Add more languages
          </button>
          {showTranslations && (
            <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <TranslationsEditor
                translations={local.translations ?? {
                  en: { label: local.label, placeholder: local.placeholder ?? undefined },
                }}
                onChange={(t) => set({ translations: t })}
              />
            </div>
          )}
        </div>

        {/* Advanced (collapsible) */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
          >
            {showAdvanced ? '▼' : '▶'} Advanced
          </button>
          {showAdvanced && (
            <div className="mt-2 space-y-3">
              <IconUploader
                label="Field Icon"
                value={local.icon ?? ''}
                onChange={(url) => set({ icon: url || undefined })}
              />
            </div>
          )}
        </div>

      </div>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <div className="shrink-0 flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/80">
        <div className="flex gap-3">
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            onClick={async () => {
              if (field.isActive === false) {
                if (!await confirm(t('confirm_activate_field'))) return;
                onSetStatus(field.key, true);
              } else {
                if (!await confirm(t('confirm_deactivate_field'))) return;
                onSetStatus(field.key, false);
              }
            }}
          >
            {field.isActive === false ? 'Activate' : 'Deactivate'}
          </button>
          {field.listingColumn ? (
            <span
              className="text-sm text-gray-400 dark:text-gray-600 cursor-not-allowed"
              title="Cannot delete — mapped to listing column"
            >
              Delete
            </span>
          ) : (
            <button
              type="button"
              className="text-sm text-red-500 hover:text-red-700 transition-colors"
              onClick={async () => {
                if (!await confirm(t('confirm_delete_field'))) return;
                onDelete(field.key);
              }}
            >
              Delete
            </button>
          )}
        </div>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {ConfirmDialog}
    </div>
  );
};

export default FieldDrawer;
